import { useCallback, useMemo } from 'react';

import { useTranslation } from '@/i18n';
import { SCREEN_PREFERENCE_CODES } from '@/routes';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { holidayService } from '@/api/domains/attendance';
import type { HolidayByRegionItem } from '@/api/domains/attendance';
import { geographyService } from '@/api/domains/geography';
import type { Region } from '@/api/domains/geography';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { formatToDdMmYyyy } from '@/utils';
import { useHolidayByRegionExport } from './useHolidayByRegionExport';


type HolidayByRegionFilters = {
  year: number | null;
  month: number | null;
  regionId: number | null;
  holidayTypeId: number | null;
};

export interface UseHolidayByRegionListResult {
  rows: HolidayByRegionItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<HolidayByRegionItem>[];
  actions: TableAction<HolidayByRegionItem>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
  refetch: () => void;
  openCreatePanel: () => void;
  filters: HolidayByRegionFilters;
  setFilters: (filters: HolidayByRegionFilters) => void;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  regionOptions: { value: string; label: string }[];
  onExport: () => Promise<void>;
  isExporting: boolean;
}

export function useHolidayByRegionList(): UseHolidayByRegionListResult {
  const { showApiError, showSuccess, showError, openModal, openSidePanel } = useUI();
  const { t: translate } = useTranslation();
  const { canEdit, canDelete, canCreate } = usePagePermissions();

  const regionCatalog = useCatalogLoader<Region>(
    (_signal) => geographyService.getRegions('CL'),
    (item) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    () => showError(translate('attendance:holiday.messages.loadErrorByRegion')),
  );

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<HolidayByRegionFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<HolidayByRegionItem> | null> => {
      const response = await holidayService.listByRegion(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          sort: params.sort
            ? {
              key: params.sort.key,
              direction: params.sort.direction.toLowerCase() as 'asc' | 'desc',
            }
            : undefined,
          year: params.filters.year ?? undefined,
          month: params.filters.month ?? undefined,
          regionId: params.filters.regionId ?? undefined,
          holidayTypeId: params.filters.holidayTypeId ?? undefined,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('attendance:holiday.messages.loadErrorByRegion'));
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    refetch,
    filters,
    setFilters,
    debouncedSearchText,
    sort,
  } = usePaginatedList<HolidayByRegionItem, HolidayByRegionFilters>({
    fetchFn,
    initialFilters: { year: null, month: null, regionId: null, holidayTypeId: null },
    screenCode: SCREEN_PREFERENCE_CODES.FESTIVOS_INHABILES_BY_REGION,
  });

  const { onExport, isExporting } = useHolidayByRegionExport({
    searchText: debouncedSearchText,
    sort,
    year: filters.year,
    month: filters.month,
    regionId: filters.regionId,
    holidayTypeId: filters.holidayTypeId,
  });

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => currentYear - 4 + i);
    return [
      { value: '', label: translate('attendance:holiday.filters.allYears') },
      ...years.map((y) => ({ value: String(y), label: String(y) })),
    ];
  }, [translate]);

  const monthOptions = useMemo(
    () => [
      { value: '', label: translate('attendance:holiday.filters.allMonths') },
      ...Array.from({ length: 12 }, (_, i) => ({
        value: String(i + 1),
        label: translate(`attendance:holiday.filters.months.${i + 1}`),
      })),
    ],
    [translate],
  );

  const regionOptions = useMemo(
    () => [
      { value: '', label: translate('attendance:holiday.filters.allRegions') },
      ...regionCatalog.options,
    ],
    [regionCatalog.options, translate],
  );

  const handleEdit = useCallback(
    (row: HolidayByRegionItem) => {
      openSidePanel({
        title: translate('attendance:holiday.form.editByRegionTitle'),
        component: 'HolidayByRegionFormPanel',
        props: { holidayByRegionData: row, onSubmit: refetch },
      });
    },
    [openSidePanel, translate, refetch],
  );

  const handleDelete = useCallback(
    (row: HolidayByRegionItem) => {
      openModal('ConfirmModal', {
        variant: 'error',
        icon: 'alert',
        title: translate('attendance:holiday.modal.deleteByRegionTitle'),
        bannerText: formatToDdMmYyyy(row.date),
        bodyText: translate('attendance:holiday.modal.deleteByRegionBody'),
        onConfirm: async () => {
          const response = await holidayService.deleteByRegion(row.id);
          if (!response.success) {
            showApiError(response, translate('attendance:holiday.messages.deleteByRegionError'));
            return;
          }
          showSuccess(translate('attendance:holiday.messages.deleteByRegionSuccess'));
          refetch();
        },
      });
    },
    [openModal, translate, showApiError, showSuccess, refetch],
  );

  const columns: TableColumn<HolidayByRegionItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('attendance:holiday.fields.id'),
        sortable: true,
      },
      {
        key: 'reason',
        header: translate('attendance:holiday.fields.reason'),
        sortable: true,
      },
      {
        key: 'type',
        header: translate('attendance:holiday.fields.type'),
        sortable: true,
      },
      {
        key: 'region',
        header: translate('attendance:holiday.fields.region'),
        sortable: true,
      },
      {
        key: 'date',
        header: translate('attendance:holiday.fields.date'),
        sortable: true,
        render: (item) => formatToDdMmYyyy(item.date),
      },
    ],
    [translate],
  );

  const actions: TableAction<HolidayByRegionItem>[] = useMemo(
    () => [
      {
        id: 'edit',
        label: translate('common:tableActions.edit'),
        icon: 'edit',
        hidden: () => !canEdit,
        onClick: handleEdit,
      },
      {
        id: 'delete',
        label: translate('common:tableActions.delete'),
        icon: 'close',
        hidden: () => !canDelete,
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [translate, canEdit, canDelete, handleEdit, handleDelete],
  );

  const openCreatePanel = useCallback(() => {
    if (!canCreate) return;
    openSidePanel({
      title: translate('attendance:holiday.form.createByRegionTitle'),
      component: 'HolidayByRegionFormPanel',
      props: { onSubmit: refetch },
    });
  }, [canCreate, openSidePanel, translate, refetch]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    refetch,
    openCreatePanel,
    filters,
    setFilters,
    yearOptions,
    monthOptions,
    regionOptions,
    onExport,
    isExporting,
  };
}

export default useHolidayByRegionList;
